import { useRollbar } from '@rollbar/react'
import { fetchBlocksSuccess } from 'client/actions/blockActions'
import * as blocksApi from 'client/api/blockApi'
import useBlocks, { selectors as blocksSelectors } from 'client/hooks/useBlocks'
import { PageTypeEnum } from 'common/enums/PageTypeEnum'
import { InternalError } from 'common/errors'
import { useActions } from 'common/hooks/useActions'
import React, { useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { BlockCategories } from './components/block-categories'
import { BlocksListing } from './components/blocks-listing'
import { BlockCategoryType } from './types'

interface BlockCategoriesProps {
  pageType: PageTypeEnum
}

export const Blocks = ({ pageType }: BlockCategoriesProps) => {
  const [activeCategory, setActiveCategory] =
    useState<BlockCategoryType | null>(null)

  const { isFetched } = useBlockCategories()

  if (!isFetched) {
    return <BlocksSkeleton />
  }

  return activeCategory ? (
    <BlocksListing
      activeCategory={activeCategory}
      setActiveCategory={setActiveCategory}
    />
  ) : (
    <BlockCategories
      pageType={pageType}
      setActiveCategory={setActiveCategory}
    />
  )
}

const BlocksSkeleton = () => {
  return (
    <>
      {Array(8)
        .fill(0)
        .map((_, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: no other key
            key={i}
            className="flex cursor-pointer items-center justify-between py-2.5 text-L transition-[all_0.3s_cubic-bezier(0,0,0,1)] hover:underline"
          >
            <Skeleton width={i % 2 === 0 ? 150 : 200} />
          </div>
        ))}
    </>
  )
}

const useBlockCategories = () => {
  const isFetched = useBlocks(blocksSelectors.isFetched)
  const dispatchFetchBlocksSuccess = useActions(fetchBlocksSuccess)
  const rollbar = useRollbar()

  const fetchBlocks = useCallback(async () => {
    try {
      const { data } = await blocksApi.fetchBlocks()
      dispatchFetchBlocksSuccess(data)
    } catch (error) {
      if (error instanceof InternalError) {
        rollbar.error(error)
      }
    }
  }, [rollbar, dispatchFetchBlocksSuccess])

  useEffect(() => {
    if (!isFetched) {
      fetchBlocks()
    }
  }, [fetchBlocks, isFetched])

  return { isFetched }
}
